<script setup lang="ts">
import { cn } from '@/lib/utils'
import { useAttrs } from 'vue'

const props = withDefaults(
  defineProps<{
    direction?: 'left' | 'up'
    pauseOnHover?: boolean
    reverse?: boolean
    fade?: boolean
    innerClassName?: string
    numberOfCopies?: number
  }>(),
  {
    direction: 'left',
    pauseOnHover: false,
    reverse: false,
    fade: false,
    numberOfCopies: 2,
  },
)

const attrs = useAttrs()
</script>

<template>
  <div
    class="marquee"
    :class="
      cn(
        'group flex gap-[1rem] overflow-hidden',
        {
          'marquee-flex-row': props.direction === 'left',
          'marquee-flex-column': props.direction !== 'left',
        },
        attrs?.class as string | undefined,
      )
    "
    :style="{
      maskImage: props.fade
        ? `linear-gradient(${
          props.direction === 'left' ? 'to right' : 'to bottom'
        }, transparent 0%, rgba(0, 0, 0, 1.0) 10%, rgba(0, 0, 0, 1.0) 90%, transparent 100%)`
        : '',
      WebkitMaskImage: props.fade
        ? `linear-gradient(${
          props.direction === 'left' ? 'to right' : 'to bottom'
        }, transparent 0%, rgba(0, 0, 0, 1.0) 10%, rgba(0, 0, 0, 1.0) 90%, transparent 100%)`
        : '',
    }"
  >
    <div
      v-for="(_, i) in Array(props.numberOfCopies).fill(0)"
      :key="i"
      class="flex ml-[2rem] shrink-0"
    >
      <div
        :class="
          cn(
            'flex justify-around gap-[4rem] [--gap:4rem] shrink-0',
            props.direction === 'left'
              ? 'animate-marquee-left flex-row'
              : 'animate-marquee-up',
            props.pauseOnHover
              && 'group-hover:[animation-play-state:paused]',
            props.reverse && 'direction-reverse',
            props.innerClassName,
          )
        "
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.marquee {
  --animate-marquee-left: marquee-left var(--duration,30s)linear infinite;
  --animate-marquee-up: marquee-up var(--duration,30s)linear infinite;
}

.animate-marquee-left {
  animation: var(--animate-marquee-left);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}
.animate-marquee-up{
  animation: var(--animate-marquee-up);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}
.running {
  animation-play-state: running;
}

.paused {
  animation-play-state: paused;
}

@media (hover:hover){
  .group-hover\:\[animation-play-state\:paused\]:is(:where(.group):hover *){
    animation-play-state:paused;
  }
}
</style>

<style>
@keyframes marquee-left {
    from {
        transform: translate3d(0, 0, 0);
    }
    to {
        transform: translate3d(calc(-100% - var(--gap) + 2rem), 0, 0);
    }
}

@keyframes marquee-up {
    from {
        transform: translate3d(0, 0, 0);
    }
    to {
        transform: translate3d(0, calc(-100% - var(--gap)), 0);
    }
}
</style>
